import React from 'react';
import styled from 'styled-components';
import { createFragmentContainer, graphql } from 'react-relay';
import { ResponsiveBar } from '@nivo/bar';
import { SubscriptionOverview_subscription } from './__generated__/SubscriptionOverview_subscription.graphql';
import Spacer from './Spacer';
import { Link } from 'found';
import { routes } from '../routes';
import moment from 'moment';

const Container = styled.div`
  width: 368px;
  border: 1px solid #f0f1f7;
  border-radius: 10px;
  padding: 23px;
  transition: all 0.2s;
  cursor: pointer;
  &:hover {
    box-shadow: 0px 3px 16px #0000000a;
  }
`;

const Graph = styled.div`
  height: 150px;
`;

const SubscriptionInfo = styled.div`
  margin-top: 20px;
  padding: 10px 0px;
  display: flex;
`;

const SubscriptionName = styled.div`
  display: flex;
  align-items: center;
`;

const Image = styled.div<{ url: string }>`
  background-image: url(${(props) => props.url});
  height: 40px;
  width: 40px;
  background-size: cover;
  border-radius: 10px;
`;

const Title = styled.div`
  font-weight: 700;
`;

const LastUsed = styled.div`
  margin-left: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Label = styled.div`
  font-size: 11px;
`;

const LastUsedText = styled.div`
  font-size: 13px;
  font-weight: 600;
`;

const Tooltip = styled.div`
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0px 2px 19px #00000014;
  border-radius: 10px;
  background: white;
`;

const TooltipLabels = styled.div`
  display: flex;
  padding: 14px;
  justify-content: space-between;
`;

const LabelCircle = styled.div`
  width: 15px;
  height: 15px;
  background: #ced0db;
  border-radius: 100%;
`;

const LabelText = styled.div``;

const LabelValue = styled.div`
  color: #9092c1;
  margin-left: auto;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: #ecedf7;
`;

const DateSection = styled.div`
  color: #9092c1;
  display: flex;
  padding: 14px;
`;

const CostItems = styled.div`
  display: flex;
  flex-direction: column;
`;

const CostItem = styled.div`
  display: flex;
`;

const TooltipDay = styled.div``;

const TooltipInterval = styled.div``;

type Props = {
  subscription?: SubscriptionOverview_subscription;
};

function SubscriptionOverview({ subscription }: Props) {
  const data = (subscription?.usage?.intervals ?? []).map((i) => {
    const total = i.totalCosts.reduce((prev, current) => {
      return prev + current.value;
    }, 0);
    return { y: total !== 0 ? total : 0, id: i.startDate, ...i };
  });

  const CustomBarComponent = (k: any) => {
    return (
      <rect
        width={k.width}
        height={k.height !== 0 ? k.height : 10}
        x={k.x}
        y={k.height !== 0 ? k.y : 146}
        fill={k.color}
        onMouseMove={(event) => {
          k.showTooltip(k.tooltip(k.data), event);
        }}
        onMouseLeave={(event) => {
          k.hideTooltip();
        }}
      />
    );
  };

  return (
    <Link to={`${routes.subscriptions}/${subscription?.id}`}>
      <Container>
        <Graph>
          <ResponsiveBar
            data={data}
            theme={{
              tooltip: {
                container: {},
              },
            }}
            barComponent={CustomBarComponent}
            keys={['y']}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            colors={'#ECEDF4'}
            padding={0.2}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            axisTop={null}
            tooltip={(info) => {
              return (
                <Tooltip>
                  <TooltipLabels>
                    <LabelCircle />
                    <Spacer horizontal spacing={2} />
                    <CostItems>
                      {(info.data.totalCosts as any).map((i: any) => (
                        <CostItem>
                          <LabelText>Usage ({i.currency})</LabelText>
                          <Spacer horizontal spacing={1} />
                          <LabelValue>{i.value}</LabelValue>
                        </CostItem>
                      ))}
                      {(info.data.totalCosts as any).length === 0 && (
                        <CostItem>
                          <LabelText>Usage</LabelText>
                          <Spacer horizontal spacing={1} />
                          <LabelValue>0</LabelValue>
                        </CostItem>
                      )}
                    </CostItems>
                  </TooltipLabels>
                  <Divider />
                  <DateSection>
                    <TooltipDay>
                      {moment(info.data.startDate).format('MMM DD')}
                    </TooltipDay>
                    <Spacer horizontal spacing={2} />
                    <TooltipInterval>
                      {moment(info.data.startDate).format('LT')} -{' '}
                      {moment(info.data.endDate).format('LT')}
                    </TooltipInterval>
                  </DateSection>
                </Tooltip>
              );
            }}
            axisRight={null}
            axisBottom={null}
            axisLeft={null}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            layers={['axes', 'bars', 'markers']}
            animate={true}
            motionStiffness={90}
            motionDamping={15}
          />
        </Graph>
        <SubscriptionInfo>
          <SubscriptionName>
            <Image url={subscription?.service?.imageURL ?? ''} />
            <Spacer horizontal spacing={2} />
            <Title>{subscription?.service.name}</Title>
          </SubscriptionName>
          <LastUsed>
            <Label>Subscribed</Label>
            <LastUsedText>
              {moment(subscription?.createdAt).fromNow()}
            </LastUsedText>
          </LastUsed>
        </SubscriptionInfo>
      </Container>
    </Link>
  );
}

export default createFragmentContainer(SubscriptionOverview, {
  subscription: graphql`
    fragment SubscriptionOverview_subscription on Subscription {
      id
      createdAt
      service {
        name
        imageURL
      }
      usage(before: $before, after: $after, intervalSeconds: 3600) {
        intervals {
          startDate
          endDate
          totalCosts {
            value
            currency
          }
        }
      }
    }
  `,
});
